import { isRejectedWithValue, type Middleware } from '@reduxjs/toolkit';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { toast } from 'sonner';
import { appInsights } from '@/telemetry';

// Status codes the UI handles per-call (LoginPage, etc.). Don't toast or
// log these as exceptions — they're expected branches.
const EXPECTED_STATUSES = new Set<number | string>([400, 401, 403, 404, 409, 429]);

// RTK Query middleware: every endpoint that fails dispatches a rejected action
// with `meta.rejectedWithValue = true` and `payload` = FetchBaseQueryError.
// We log every failure to App Insights and pop a toast for unexpected ones.
export const rysErrorMiddleware: Middleware = () => next => action => {
  if (isRejectedWithValue(action)) {
    const meta = (action as { meta?: { arg?: { endpointName?: string } } }).meta;
    const endpoint = meta?.arg?.endpointName ?? 'unknown';
    const payload = (action as { payload?: { status?: number | string; data?: unknown; error?: string } }).payload;
    const status = payload?.status;
    const expected = status !== undefined && EXPECTED_STATUSES.has(status);

    // Expected statuses (404 for an absent fund logo, 401 before reauth, etc.)
    // are normal control flow — log them as informational traces, not
    // exceptions, so telemetry isn't polluted with non-failures.
    if (expected) {
      appInsights?.trackTrace({
        message: `RTK Query ${endpoint}: ${String(status)}`,
        severityLevel: SeverityLevel.Information,
        properties: { endpoint, status, payload: safeStringify(payload) },
      });
    } else {
      appInsights?.trackException({
        exception: new Error(`RTK Query ${endpoint} failed: ${String(status)}`),
        properties: { endpoint, status, payload: safeStringify(payload) },
      });
      toast.error(`Something went wrong (${endpoint}).`);
    }
  }
  return next(action);
};

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
